// umlaut-check: skip — technische Bezeichner (verfuegbar) der Bestandskennzahlen.
import React, {useCallback, useRef, useState} from 'react';
import axios from 'axios';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import {TOOLTIP_DELAY, fixedPopper} from '../tooltipConfig';
import {BEGRIFF, BESTAND_ERKLAERUNG, OFFEN_NICHT_RESERVIERT_ERKLAERUNG, menge} from './bestandTexte';

/**
 * Kennzahlen des Servers (AvailabilityService) in die Eigenschaften des Tooltips übersetzen.
 *
 * @param {object|undefined} k - lagerbestand, reserviert_fremd, reserviert_eigen, verfuegbar, offen_nicht_reserviert
 */
export function werteAusKennzahlen(k) {
    if (!k) {
        return {};
    }
    return {
        lagerbestand: k.lagerbestand,
        reserviertFremd: k.reserviert_fremd,
        reserviertEigen: k.reserviert_eigen,
        verfuegbar: k.verfuegbar,
        offenNichtReserviert: k.offen_nicht_reserviert,
    };
}

/**
 * Gemeinsamer Tooltip für JEDE Bestandszahl (Auftrag #315): fester
 * Erklärtext „Aufträge reservieren Bestand“, die aufgeschlüsselten Zahlen und
 * — wo die Daten da sind — wer die Ware reserviert hält (LS-/AB-Nummern).
 * Die Halter werden erst beim Öffnen des Tooltips geladen.
 *
 * Keine nativen Browser-Tooltips: react-bootstrap-Overlay mit Tastaturfokus.
 *
 * @param {object} werte
 * @param {number} [werte.lagerbestand]
 * @param {number} [werte.reserviertFremd]   - von anderen Aufträgen reserviert
 * @param {number} [werte.reserviertEigen]   - von diesem Auftrag reserviert
 * @param {number} [werte.verfuegbar]
 * @param {number} [werte.offenNichtReserviert]
 * @param {string} [lagerName]
 * @param {string} [quelle]      - Herkunft der Zahlen, z. B. „live aus Weclapp“ oder „Stand …“
 * @param {string} [haelterUrl]  - GET-Adresse, die {holders: [...]} liefert; ohne Angabe keine Halterliste
 * @param {object} [haelterParams]
 * @param {React.ReactNode} [kopf]   - Zeile(n) vor den Zahlen (z. B. „Ab Lahr liefern.“)
 * @param {React.ReactNode} [zusatz] - zusätzlicher Absatz (z. B. Hinweis zur Planungssicht)
 * @param {string} [placement]
 * @param {React.ReactElement} children - genau ein Element
 */
export default function BestandTooltip({
    werte = {},
    lagerName,
    quelle,
    haelterUrl,
    haelterParams,
    kopf,
    zusatz,
    placement = 'top',
    children,
}) {
    const [halter, setHalter] = useState(null);
    const [fehler, setFehler] = useState(null);
    const geladen = useRef(false);

    const laden = useCallback((offen) => {
        if (!offen || !haelterUrl || geladen.current) {
            return;
        }
        geladen.current = true;
        axios.get(haelterUrl, {params: haelterParams})
            .then(({data}) => setHalter(Array.isArray(data?.holders) ? data.holders : []))
            .catch(() => {
                geladen.current = false;
                setFehler('Die reservierenden Belege konnten nicht geladen werden.');
            });
    }, [haelterUrl, haelterParams]);

    const {lagerbestand, reserviertFremd, reserviertEigen, verfuegbar, offenNichtReserviert} = werte;
    const hatFremd = Number(reserviertFremd) > 0;
    const fremdeHalter = (halter || []).filter((h) => !h.eigen);
    const veralteteHalter = fremdeHalter.filter((h) => h.veraltet);

    const inhalt = (
        <Tooltip id="bestand-tooltip" className="help-tooltip" style={{textAlign: 'left'}}>
            <div style={{textAlign: 'left', maxWidth: 340}}>
                {kopf ? <div style={{marginBottom: 4}}>{kopf}</div> : null}
                {lagerName ? <div><b>{lagerName}</b></div> : null}
                {lagerbestand !== undefined ? <div>{BEGRIFF.lagerbestand}: <b>{menge(lagerbestand)}</b> (physisch im Lager)</div> : null}
                {reserviertFremd !== undefined ? <div>{BEGRIFF.reserviert} (andere Aufträge): <b>{menge(reserviertFremd)}</b></div> : null}
                {Number(reserviertEigen) > 0 ? <div>davon für diesen Auftrag {BEGRIFF.reserviert}: <b>{menge(reserviertEigen)}</b></div> : null}
                {verfuegbar !== undefined ? <div>{BEGRIFF.verfuegbar}: <b>{menge(verfuegbar)}</b></div> : null}
                {offenNichtReserviert !== undefined && offenNichtReserviert !== null ? (
                    <div style={{opacity: 0.75}}>{BEGRIFF.offenNichtReserviert}: {menge(offenNichtReserviert)}</div>
                ) : null}

                {haelterUrl && hatFremd ? (
                    <div style={{marginTop: 6}}>
                        {fehler ? <span>{fehler}</span> : null}
                        {!fehler && halter === null ? <span>Reservierende Belege werden geladen …</span> : null}
                        {!fehler && halter !== null && fremdeHalter.length ? (
                            <span>
                                Reserviert durch:{' '}
                                {fremdeHalter.map((h, i) => (
                                    <span key={`${h.nummer}-${i}`}>
                                        {i ? ', ' : ''}{h.nummer}{h.auftrag ? ` (${h.auftrag})` : ''} {menge(h.menge)}
                                        {h.veraltet ? <b style={{color: '#b45309'}}> ⚠ seit {h.seitTagen} Tagen offen</b> : null}
                                    </span>
                                ))}
                            </span>
                        ) : null}
                        {!fehler && veralteteHalter.length ? (
                            <div style={{marginTop: 4}}>
                                ⚠ Mit „seit … Tagen offen" markierte Lieferscheine stehen schon lange im Status „Neu"
                                oder „gedruckt" und halten ihre Reservierung vermutlich ohne Grund. Sie werden nicht
                                automatisch gelöst — bitte in weclapp prüfen und ggf. versenden oder stornieren.
                            </div>
                        ) : null}
                        {!fehler && halter !== null && !fremdeHalter.length ? (
                            <span>Die reservierenden Belege liegen gerade nicht vor.</span>
                        ) : null}
                    </div>
                ) : null}

                <div style={{marginTop: 6}}>{BESTAND_ERKLAERUNG}</div>
                {offenNichtReserviert !== undefined && offenNichtReserviert !== null ? (
                    <div style={{marginTop: 6}}>{OFFEN_NICHT_RESERVIERT_ERKLAERUNG}</div>
                ) : null}
                {zusatz ? <div style={{marginTop: 6}}>{zusatz}</div> : null}
                {quelle ? <div style={{marginTop: 6, opacity: 0.75}}>{quelle}</div> : null}
            </div>
        </Tooltip>
    );

    return (
        <OverlayTrigger
            placement={placement}
            flip
            delay={TOOLTIP_DELAY}
            onToggle={laden}
            popperConfig={fixedPopper([
                {name: 'flip', options: {fallbackPlacements: ['top', 'bottom', 'right', 'left'], padding: 8}},
                {name: 'preventOverflow', options: {padding: 8}},
            ])}
            overlay={inhalt}
        >
            {children}
        </OverlayTrigger>
    );
}
